import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import StarRating from '../components/StarRating';

const RATING_LABELS = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very good', 5: 'Excellent' };

export default function FeedbackPage() {
  const { user }              = useAuth();
  const [form, setForm]       = useState({ feedback_text: '', rating: 0 });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rating) { setError('Please select a star rating.'); return; }
    if (!form.feedback_text.trim()) { setError('Please write some feedback.'); return; }
    setError('');
    setLoading(true);
    const { error: err } = await supabase
      .from('feedback')
      .insert({ user_id: user?.id, feedback_text: form.feedback_text.trim(), rating: form.rating });
    if (err) {
      setError('Submission failed. Please try again.');
    } else {
      setSuccess(true);
      setForm({ feedback_text: '', rating: 0 });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-0.5">Share Your Feedback</h3>
        <p className="text-gray-500 text-sm mb-7">Your thoughts directly shape how we improve the platform.</p>

        {success ? (
          <div className="text-center py-8">
            <p className="text-6xl mb-4">🎉</p>
            <h4 className="text-xl font-bold text-gray-900 mb-2">Thank you!</h4>
            <p className="text-gray-500 text-sm mb-6">Your feedback means a lot to us.</p>
            <button
              onClick={() => setSuccess(false)}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Submit Another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Star rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Overall Rating
                {form.rating > 0 && (
                  <span className="ml-2 text-indigo-600 font-normal">
                    — {RATING_LABELS[form.rating]}
                  </span>
                )}
              </label>
              <StarRating
                value={form.rating}
                onChange={(val) => setForm((f) => ({ ...f, rating: val }))}
              />
            </div>

            {/* Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Your Feedback
              </label>
              <textarea
                value={form.feedback_text}
                onChange={(e) => setForm((f) => ({ ...f, feedback_text: e.target.value }))}
                rows={5}
                placeholder="Tell us what you love, what you'd change, or anything on your mind…"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition text-sm"
              />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition-colors"
            >
              {loading ? 'Submitting…' : 'Submit Feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
